import type {
    IInventoryFilters,
    IInventorySummaryItem,
} from "@agrigate/commodity-transaction-api/lib/services/InventoryService";
import { type ISettlementUnit, Perishability } from "@agrigate/domain";
import type { IExtProduceType } from "@agrigate/product-catalog-types";
import { formatToWeightUnits } from "@agrigate/reporting-ui/lib/utils/formatters";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import { formatMoney } from "@agrigate/shared/lib/utils/formatters";
import { getBatchSize } from "@agrigate/shared/lib/utils/quantity";
import BlockDropDown from "@agrigate/shared-web-ui/lib/BlockDropDown";
import ClusterDropDown from "@agrigate/shared-web-ui/lib/ClusterDropDown";
import type { ContentCardProps } from "@agrigate/shared-web-ui/lib/ContentCard";
import ContentCard from "@agrigate/shared-web-ui/lib/ContentCard";
import DistrictDropDown from "@agrigate/shared-web-ui/lib/DistrictDropDown";
import FormItem from "@agrigate/shared-web-ui/lib/FormItem";
import { useIsDesktop } from "@agrigate/shared-web-ui/lib/hooks/use-is-desktop";
import PopoverCombo from "@agrigate/shared-web-ui/lib/PopoverCombo";
import RegionDropDown from "@agrigate/shared-web-ui/lib/RegionDropdown";
import SwitchButtonCombo from "@agrigate/shared-web-ui/lib/SwitchButtonCombo";
import ChakraTable, {
    type ColDef,
} from "@agrigate/shared-web-ui/src/ChakraTable";
import { toNum } from "@agrigate/ts-utils/lib/coercions";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import {
    Accordion,
    Box,
    Button,
    Flex,
    Heading,
    Icon,
    Separator,
    Spacer,
    Text,
} from "@chakra-ui/react";
import type { ApexOptions } from "apexcharts";
import { compact, groupBy, startCase, sum } from "lodash";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import ApexCharts from "react-apexcharts";
import { useForm } from "react-hook-form";
import { IoChevronDown, IoFilter, IoRefresh } from "react-icons/io5";
import LocationOnIcon from "../assets/icons/location-on-icon.svg?react";
import OrderDetailsIcon from "../assets/icons/order-details-icon.svg?react";
import ProduceTypeDropdown from "../components/ProduceTypeDropdown";
import ProduceTypeVarietiesDropdown from "../components/ProduceTypeVarietiesDropdown";
import ProductLabel from "../components/ProductLabel";
import rpc from "../utils/rpc";
import CollectionCentersDropdown from "./CollectionCentersDropdown";

// Types
interface PTStorageSummary {
    id: string;
    produceType: MaybeN<IExtProduceType>;
    locationCount: number;
    weightKg: number;
    numBatches: number;
    amount: number;
    settlementUnit: ISettlementUnit;
}

interface InventoryOverviewProps {
    extFilters?: IInventoryFilters;
    setData?: React.Dispatch<
        React.SetStateAction<MaybeN<IInventorySummaryItem[]>>
    >;
    filterStyle?: {
        heading?: string;
        borderRadius?: number;
        cardElevation?: ContentCardProps["elevation"];
    };
    pType?: string;
    setPType?: (ptype: string) => void;
}

const formatQuantity = (
    settlementUnit: ISettlementUnit,
    weightKg: number,
    numBatches: number,
): string => {
    const batchSize = getBatchSize(settlementUnit);
    if (settlementUnit === "KG") {
        return formatToWeightUnits(weightKg);
    } else if (batchSize) {
        return `${numBatches * batchSize} ${"Item"}${numBatches !== 1 ? "s" : ""}`;
    } else {
        return "";
    }
};

// Inventory Overview Column List
const storageSummaryCols: ColDef<PTStorageSummary>[] = [
    {
        label: "Product",
        key: "pt",
        flexGrow: 1,
        render: v => <ProductLabel produceType={v.produceType} />,
    },
    {
        label: "Locations",
        key: "locationCount",
    },
    {
        label: "Quantity",
        key: "weightKg",
        render: v => (
            <div style={{ width: "100%", textAlign: "right" }}>
                {formatQuantity(v.settlementUnit, v.weightKg, v.numBatches)}
            </div>
        ),
    },
    {
        label: "Amount",
        key: "amount",
        render: v => (
            <div
                style={{
                    width: "100%",
                    textAlign: "right",
                    paddingRight: "10px",
                }}
            >
                {formatMoney(v.amount)}
            </div>
        ),
    },
];

// Utility functions
const calculateStorageSummary = (
    entries: [string, IInventorySummaryItem[]][],
): PTStorageSummary[] =>
    compact(
        entries.map(([, v]) => {
            const pt = v[0]?.produceType?.value;
            if (!pt) return null;
            const settlementUnit = pt.settlementUnit ?? "KG";
            let totalWeightKg = 0;
            let totalNumBatches = 0;

            if (settlementUnit === "KG") {
                totalWeightKg = sum(v.map(item => toNum(item.weightKg ?? 0)));
            } else {
                totalNumBatches = sum(
                    v.map(item => toNum(item.numBatches ?? 0)),
                );
            }

            return {
                id: pt.id,
                produceType: pt,
                locationCount: v.length,
                weightKg: totalWeightKg,
                numBatches: totalNumBatches,
                settlementUnit: settlementUnit,
                amount: sum(
                    v.map(item => {
                        if (settlementUnit === "KG" && item.avgPricePerKg) {
                            return (
                                toNum(item.avgPricePerKg) *
                                toNum(item.weightKg ?? 0)
                            );
                        } else if (item.avgPricePerBatch) {
                            return (
                                toNum(item.avgPricePerBatch) *
                                (item.numBatches ?? 0)
                            );
                        }
                        return 0;
                    }),
                ),
            };
        }),
    );

const calculateOverviewSeries = (
    entries: [string, IInventorySummaryItem[]][],
) => {
    const amounts = entries.map(([, v]) => {
        const pt = v[0]?.produceType?.value;
        const settlementUnit = pt?.settlementUnit ?? "KG";

        return sum(
            v.map(item => {
                if (settlementUnit === "KG" && item.avgPricePerKg) {
                    return (
                        toNum(item.weightKg ?? 0) * toNum(item.avgPricePerKg)
                    );
                } else if (item.avgPricePerBatch) {
                    return (
                        toNum(item.numBatches ?? 0) *
                        toNum(item.avgPricePerBatch)
                    );
                } else return 0;
            }),
        );
    });

    const total = sum(amounts);
    return amounts.map(amount => (total ? (100 * amount) / total : 0));
};

// Components
export const InventoryFilterForm: React.FC<{
    filters: IInventoryFilters;
    setFilters: (filters: IInventoryFilters) => void;
    onClear?: () => void;
    onExit: () => void;
}> = ({ filters, setFilters, onClear, onExit }) => {
    const { watch, setValue, handleSubmit, reset } = useForm({
        defaultValues: {
            blockId: filters.blockId ?? "",
            regionId: filters?.regionId ?? "",
            districtId: filters?.districtId ?? "",
            clusterId: filters?.clusterId ?? "",
            collectionCenterId: filters?.collectionCenterId ?? "",
            produceTypeId: filters?.produceTypeId ?? "",
            varietyId: filters?.varietyId ?? "",
        },
    });

    const [
        regionId,
        districtId,
        clusterId,
        blockId,
        collectionCenterId,
        produceTypeId,
        varietyId,
    ] = watch([
        "regionId",
        "districtId",
        "clusterId",
        "blockId",
        "collectionCenterId",
        "produceTypeId",
        "varietyId",
    ]);

    const handleFiltersSubmit = (data: Record<string, string>) => {
        const filters = {
            regionId: data.regionId ? data.regionId : undefined,
            districtId: data.districtId ? data.districtId : undefined,
            clusterId: data.clusterId ? data.clusterId : undefined,
            collectionCenterId: data.collectionCenterId
                ? data.collectionCenterId
                : undefined,
            produceTypeId: data.produceTypeId ? data.produceTypeId : undefined,
            varietyId: data.varietyId ? data.varietyId : undefined,
        };
        setFilters(filters);
        onExit?.();
    };

    const handleReset = () => {
        reset();
        setFilters({});
        onClear?.();
        onExit?.();
    };

    return (
        <form onSubmit={handleSubmit(handleFiltersSubmit)}>
            <Flex
                boxShadow="md"
                maxWidth="800px"
                mx="auto"
                bgColor={"gray.050"}
                flexDir={"column"}
                height={"100%"}
            >
                <Flex
                    p={4}
                    css={{
                        "&::-webkit-scrollbar": {
                            width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#888",
                            borderRadius: "4px",
                        },
                    }}
                >
                    <Accordion.Root
                        collapsible
                        multiple
                        defaultValue={["location", "product"]}
                    >
                        <Accordion.Item
                            border={"none"}
                            backgroundColor={"white"}
                            borderRadius={"md"}
                            p={2}
                            value="location"
                            marginBottom={"10px"}
                        >
                            <Accordion.ItemTrigger
                                px={3}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Flex alignItems="center" gap={2}>
                                    <LocationOnIcon />
                                    <Text fontSize={"sm"} fontWeight="medium">
                                        Location
                                    </Text>
                                </Flex>
                                <Flex alignItems="center" gap={2}>
                                    <Icon
                                        as={IoRefresh}
                                        cursor="pointer"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setValue("regionId", "");
                                            setValue("districtId", "");
                                            setValue("blockId", "");
                                            setValue("clusterId", "");
                                            setValue("collectionCenterId", "");
                                        }}
                                        color={"black"}
                                        size={"lg"}
                                        style={{
                                            borderWidth: "1px",
                                            padding: "4px",
                                            borderColor: "#f4f4f5",
                                            borderRadius: "3px",
                                        }}
                                    />
                                    <Accordion.ItemIndicator>
                                        <Icon
                                            as={IoChevronDown}
                                            color={"black"}
                                            size={"lg"}
                                            style={{
                                                borderWidth: "1px",
                                                padding: "5px",
                                                backgroundColor: "#f4f4f5",
                                                borderColor: "#f4f4f5",
                                                borderRadius: "3px",
                                            }}
                                        />
                                    </Accordion.ItemIndicator>
                                </Flex>
                            </Accordion.ItemTrigger>
                            <Accordion.ItemContent
                                borderTopWidth={"1px"}
                                borderColor="gray.200"
                                borderRadius={"0"}
                            >
                                <Flex p={2} flexDirection="column" gap={3}>
                                    <Flex gap={3}>
                                        <FormItem label="Region">
                                            <RegionDropDown
                                                width="100%"
                                                regionId={regionId}
                                                onRegionChange={region => {
                                                    setValue(
                                                        "regionId",
                                                        region || "",
                                                    );
                                                    setValue("clusterId", "");
                                                    setValue("districtId", "");
                                                    setValue(
                                                        "collectionCenterId",
                                                        "",
                                                    );
                                                }}
                                                comboRootProps={{
                                                    flex: 1,
                                                }}
                                            />
                                        </FormItem>

                                        <FormItem label="District">
                                            <DistrictDropDown
                                                regionId={regionId}
                                                districtId={districtId ?? ""}
                                                width={"100%"}
                                                disabled={!regionId}
                                                onChange={district => {
                                                    if (district) {
                                                        setValue(
                                                            "districtId",
                                                            district,
                                                        );
                                                    } else {
                                                        setValue(
                                                            "districtId",
                                                            "",
                                                        );
                                                        setValue(
                                                            "clusterId",
                                                            "",
                                                        );
                                                        setValue(
                                                            "collectionCenterId",
                                                            "",
                                                        );
                                                    }
                                                }}
                                            />
                                        </FormItem>
                                    </Flex>
                                    <Flex gap={3}>
                                        <FormItem label="Block">
                                            <BlockDropDown
                                                districtId={districtId}
                                                blockId={blockId}
                                                onChange={blockId => {
                                                    if (blockId) {
                                                        setValue(
                                                            "blockId",
                                                            blockId,
                                                        );
                                                    } else {
                                                        setValue("blockId", "");
                                                    }
                                                }}
                                                width="100%"
                                            />
                                        </FormItem>

                                        <FormItem label="Cluster">
                                            <ClusterDropDown
                                                width="100%"
                                                regionId={regionId}
                                                districtId={districtId}
                                                clusterId={clusterId}
                                                disabled={!regionId}
                                                onChange={cluster => {
                                                    if (cluster) {
                                                        setValue(
                                                            "clusterId",
                                                            cluster,
                                                        );
                                                    } else {
                                                        setValue(
                                                            "clusterId",
                                                            "",
                                                        );
                                                        setValue(
                                                            "collectionCenterId",
                                                            "",
                                                        );
                                                    }
                                                }}
                                            />
                                        </FormItem>
                                    </Flex>

                                    <FormItem label="Collection Center" py={2}>
                                        <CollectionCentersDropdown
                                            width="100%"
                                            regionId={regionId}
                                            districtId={districtId}
                                            clusterId={clusterId}
                                            value={collectionCenterId ?? ""}
                                            disabled={!regionId}
                                            onChange={cc => {
                                                if (cc)
                                                    setValue(
                                                        "collectionCenterId",
                                                        cc?.id ?? "",
                                                    );
                                            }}
                                        />
                                    </FormItem>
                                </Flex>
                            </Accordion.ItemContent>
                        </Accordion.Item>

                        <Accordion.Item
                            border={"none"}
                            backgroundColor={"white"}
                            borderRadius={"md"}
                            p={2}
                            value="product"
                        >
                            <Accordion.ItemTrigger
                                px={3}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Flex alignItems="center" gap={2}>
                                    <OrderDetailsIcon />
                                    <Text fontSize={"sm"} fontWeight="medium">
                                        Produce
                                    </Text>
                                </Flex>
                                <Flex alignItems="center" gap={2}>
                                    <Icon
                                        as={IoRefresh}
                                        cursor="pointer"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setValue("produceTypeId", "");
                                            setValue("varietyId", "");
                                        }}
                                        color={"black"}
                                        size={"lg"}
                                        style={{
                                            borderWidth: "1px",
                                            padding: "4px",
                                            borderColor: "#f4f4f5",
                                            borderRadius: "3px",
                                        }}
                                    />
                                    <Accordion.ItemIndicator>
                                        <Icon
                                            as={IoChevronDown}
                                            color={"black"}
                                            size={"lg"}
                                            style={{
                                                borderWidth: "1px",
                                                padding: "5px",
                                                backgroundColor: "#f4f4f5",
                                                borderColor: "#f4f4f5",
                                                borderRadius: "3px",
                                            }}
                                        />
                                    </Accordion.ItemIndicator>
                                </Flex>
                            </Accordion.ItemTrigger>
                            <Accordion.ItemContent
                                borderTopWidth={"1px"}
                                borderColor="gray.200"
                                borderRadius={"0"}
                                overflow="visible"
                            >
                                <Flex p={2} gap={3}>
                                    <FormItem label="Produce Type">
                                        <ProduceTypeDropdown
                                            style={{ width: "100%" }}
                                            value={produceTypeId}
                                            onChange={ptId =>
                                                setValue(
                                                    "produceTypeId",
                                                    ptId ?? "",
                                                )
                                            }
                                        />
                                    </FormItem>

                                    <FormItem label="Produce Variety">
                                        <ProduceTypeVarietiesDropdown
                                            produceTypeId={produceTypeId}
                                            value={varietyId}
                                            style={{ width: "100%" }}
                                            onChange={vId =>
                                                setValue("varietyId", vId ?? "")
                                            }
                                        />
                                    </FormItem>
                                </Flex>
                            </Accordion.ItemContent>
                        </Accordion.Item>
                    </Accordion.Root>
                </Flex>

                <Flex
                    p={4}
                    borderTop="1px solid"
                    borderColor="gray.200"
                    gap={3}
                    justifyContent="space-between"
                    bg="white"
                    borderBottomRadius="md"
                    flexShrink={0}
                    position="sticky"
                    bottom={0}
                    zIndex={2}
                >
                    <Button variant="outline" size="sm" onClick={handleReset}>
                        Reset
                    </Button>
                    <Flex gap={3}>
                        <Button variant="outline" size="sm" onClick={onExit}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            size="sm"
                            variant="solid"
                            colorPalette="green"
                        >
                            Apply
                        </Button>
                    </Flex>
                </Flex>
            </Flex>
        </form>
    );
};

const InventoryOverview: React.FC<InventoryOverviewProps> = ({
    extFilters,
    setData,
    filterStyle,
    pType,
    setPType,
}) => {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState<IInventoryFilters>(extFilters ?? {});

    const query = useMemo(
        () => ({
            localeCode: "en-IN",
            regionId: filters?.regionId ?? null,
            districtId: filters?.districtId ?? null,
            clusterId: filters?.clusterId ?? null,
            blockId: filters?.blockId ?? null,
            collectionCenterId: filters?.collectionCenterId ?? null,
            produceTypeId: filters?.produceTypeId ?? null,
            varietyId: filters?.varietyId ?? null,
        }),
        [filters],
    );

    const { data: inventoryData } = useAsyncTask(
        async () => await rpc.inventory.getSummary(query),
        {
            deps: [query],
            autoRun: true,
        },
    );

    useEffect(() => {
        if (extFilters) {
            setFilters(extFilters);
        }
    }, [extFilters]);

    useEffect(() => {
        setData?.(inventoryData?.items);
    }, [inventoryData, setData]);

    const overviewEntries = useMemo(() => {
        const rows =
            pType === "ALL"
                ? inventoryData?.items
                : (inventoryData?.items?.filter(
                      v => v.produceType?.value?.perishability === pType,
                  ) ?? []);
        return Object.entries(
            groupBy(rows, v => v.produceType?.value?.id ?? "$none"),
        );
    }, [inventoryData, pType]);

    const storageSummaryRows = useMemo(
        () => calculateStorageSummary(overviewEntries),
        [overviewEntries],
    );

    const itemOverviewSeries = useMemo(
        () => calculateOverviewSeries(overviewEntries),
        [overviewEntries],
    );

    const chartOptions = useMemo(
        (): ApexOptions => ({
            chart: {
                type: "donut",
                width: 200,
                height: 200,
            },
            legend: {
                show: false,
            },
            labels: overviewEntries.map(([_k, v]) => {
                const pt = v[0]?.produceType?.value;
                return pt?.name ?? pt?.key ?? "Unknown";
            }),
        }),
        [overviewEntries],
    );

    const isDesktop = useIsDesktop();

    return (
        <ContentCard
            elevation={filterStyle?.cardElevation ?? "nn"}
            borderWidth={0}
            borderRadius={filterStyle?.borderRadius ?? 10}
            my={extFilters ? 0 : 3}
            p={0}
            heading={
                filterStyle?.heading ?? (
                    <Heading fontSize={16} mt={10}>
                        Inventory
                    </Heading>
                )
            }
        >
            <Flex px={3} py={3}>
                <SwitchButtonCombo
                    options={["ALL", ...Object.values(Perishability.enum)].map(
                        value => ({
                            value,
                            label: startCase(value.toLowerCase()),
                        }),
                    )}
                    selected={pType}
                    onChange={val => setPType?.(val)}
                    isAttached={isDesktop}
                    styles={{
                        group: {
                            flexWrap: "wrap",
                            gap: isDesktop ? "0px" : "5px",
                        },
                    }}
                />
                <Spacer />
                {!extFilters && (
                    <PopoverCombo
                        closeOnBlur={false}
                        isOpen={filtersOpen}
                        onOpen={() => setFiltersOpen(true)}
                        onClose={() => setFiltersOpen(false)}
                        showArrow={false}
                        renderTrigger={() => (
                            <Button
                                size="sm"
                                variant="outline"
                                color="gray"
                                borderColor="gray.200"
                            >
                                {<IoFilter />}
                                Filters
                            </Button>
                        )}
                        styles={{
                            body: {
                                padding: 0,
                            },
                            content: {
                                width: "500px",
                            },
                        }}
                    >
                        <InventoryFilterForm
                            filters={filters}
                            setFilters={newFilters => {
                                setFilters(prev => ({
                                    ...prev,
                                    ...newFilters,
                                }));
                                setFiltersOpen(false);
                            }}
                            onClear={() =>
                                setFilters({
                                    regionId: null,
                                    clusterId: null,
                                })
                            }
                            onExit={() => setFiltersOpen(false)}
                        />
                    </PopoverCombo>
                )}
            </Flex>

            <Separator borderColor="gray.100" />

            <Flex direction={["column", "row"]} px={3} py={3}>
                <Box width="300px" mt={4} height="200px" alignSelf={"center"}>
                    {!!itemOverviewSeries.length && (
                        <ApexCharts
                            type="donut"
                            height="200px"
                            options={chartOptions}
                            series={itemOverviewSeries}
                        />
                    )}
                </Box>
                <ChakraTable
                    tableData={storageSummaryRows}
                    columns={storageSummaryCols}
                    height={isDesktop ? "500px" : "auto"}
                    headerHeight={60}
                    width={"100%"}
                    getSummaryHeader={row => row.produceType?.name ?? row.id}
                    renderEmpty={() => (
                        <Flex
                            align={"center"}
                            height={"100%"}
                            justify={"center"}
                            p={10}
                        >
                            <Text
                                fontSize={"md"}
                                fontWeight={"500"}
                                color={"iceGrey.500"}
                            >
                                {"Inventory is empty"}
                            </Text>
                        </Flex>
                    )}
                />
            </Flex>
        </ContentCard>
    );
};

export default InventoryOverview;
